import { AppDispatch } from "../data/redux/store";
import { logout as adminLogout } from "../data/redux/adminAuthSlice";
import { logout as authLogout, userLogout } from "../data/redux/authSlice";
import { logout as employeeLogout } from "../data/redux/employeeAuthSlice";
import { tokenService } from "./tokenService";

/**
 * Centralized Secure Logout Utility (CWE-1021 / CWE-922 Remediation)
 * Clears sensitive tokens, resets Redux states, wipes browser storages/cookies/cache,
 * and prevents browser back button navigation via history state replacement.
 */
export const performSecureLogout = async (
  dispatch?: AppDispatch,
  redirectPath: string = "/login"
) => {
  try {
    if (dispatch) {
      await dispatch(userLogout()).unwrap().catch(() => {});
    }
  } catch (err) {
    console.error("Logout API call failed or timed out:", err);
  } finally {
    // 1. Wipe in-memory authentication tokens
    tokenService.clearToken();

    // 2. Reset all Redux auth slice states if dispatch provided
    if (dispatch) {
      try {
        dispatch(adminLogout());
        dispatch(authLogout());
        dispatch(employeeLogout());
      } catch (e) {
        console.error("Error resetting Redux state:", e);
      }
    }

    // 3. Clear all browser persistent and session storages
    if (typeof window !== "undefined") {
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (e) {
        console.error("Error clearing browser storage:", e);
      }

      // 4. Wipe browser cookies
      if (document.cookie) {
        document.cookie.split(";").forEach((cookie) => {
          const name = cookie.split("=")[0].trim();
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`;
        });
      }

      // 5. Clear application caches
      if ("caches" in window) {
        try {
          const cacheKeys = await caches.keys();
          await Promise.all(cacheKeys.map((key) => caches.delete(key)));
        } catch (e) {
          // Ignore cache deletion errors
        }
      }

      // 6. Hard redirect to login and wipe browser history stack to prevent back-button traversal
      window.history.pushState(null, "", redirectPath);
      window.location.replace(redirectPath);
    }
  }
};
